/**
 * Баримт хэвлэх popup — хүргэлтийн болон буцаалтын баримт хоёулаа ашиглана.
 * Загвар нь `public/delivery-receipt-print.css`-д байна.
 */

export function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Must be called synchronously from the click handler, before any `await` — iOS Safari only
 * allows window.open() while still inside the click's call stack. Opening after a fetch gets
 * silently blocked on iPhone instead of returning null.
 */
export function openPrintWindow(): Window | null {
  const w = window.open('', '_blank');
  if (!w) {
    window.alert('Хавтсаар шинэ цонх нээгдэхгүй байна.');
  }
  return w;
}

export type PrintDocument = {
  title: string;
  /** Already-escaped markup, e.g. from ReactDOMServer.renderToStaticMarkup */
  bodyHtml: string;
};

/**
 * Writes the receipts into the popup and opens its print dialog. `onAfterPrint` runs once,
 * when the print dialog closes.
 */
export function writeAndPrint(w: Window, doc: PrintDocument, onAfterPrint?: () => void): void {
  const origin = window.location.origin;
  w.document.open();
  w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${escapeHtml(doc.title)}</title>
<link rel="stylesheet" href="${escapeHtml(origin)}/delivery-receipt-print.css">
</head><body>
${doc.bodyHtml}
</body></html>`);
  w.document.close();
  w.focus();

  if (onAfterPrint) {
    const afterPrint = () => {
      w.removeEventListener('afterprint', afterPrint);
      onAfterPrint();
    };
    w.addEventListener('afterprint', afterPrint);
  }

  // Printing before the stylesheet (and the seal image) finish loading renders a
  // blank/unstyled page on iOS Safari's stricter print pipeline — the popup itself
  // still looks right on screen because it repaints once those resources land a
  // moment later. Wait for `load` (fires once all linked resources are in), with a
  // timeout fallback in case it never fires, and guard against printing twice.
  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    w.focus();
    w.print();
  };
  if (w.document.readyState === 'complete') {
    setTimeout(triggerPrint, 50);
  } else {
    w.addEventListener('load', () => setTimeout(triggerPrint, 50));
  }
  setTimeout(triggerPrint, 3000);
}
